/* ---------------------------------------------------------------------------
(c) 2023-2024, HID Global Corporation, part of ASSA ABLOY.
All rights reserved.

This program is an un published copyrighted work which is proprietary
to HID Global Corporation. This computer program includes Confidential,
Proprietary Information and is a Trade Secret of HID Global Corporation.
Any use, disclosure, modification and/or reproduction is prohibited
unless authorized in writing by HID Global Corporation.

WARNING:  Unauthorized reproduction of this program as well as
unauthorized preparation of derivative works based upon the
program or distribution of copies by sale, rental, lease or
lending are violations of federal copyright laws and state trade
secret laws, punishable by civil and criminal penalties.
--------------------------------------------------------------------------- */
import { Injectable } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { Subject } from 'rxjs/internal/Subject';

export interface NotificationData {
  title: string;
  subtitle: string;
  type: 'info' | 'error' | 'success';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  horizontalPosition: MatSnackBarHorizontalPosition = 'right';
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  config: MatSnackBarConfig = new MatSnackBarConfig();
  errorCodes: any = {
    badRequest: 'There are invalid character(s) in the request, please correct and try again.',
    notFound: 'The resource you are looking for does not exist or is temporarily unavailable.',
    internalServerError:
      'We were unable to complete your request, please try again later. If the problem persists, please contact your administrator for further information.',
    conflict: 'The resource you are trying to create already exists.'
  };

  private notificationSubject = new Subject<NotificationData>();
  notification$ = this.notificationSubject.asObservable();

  constructor(private snackBar: MatSnackBar) {}

  callSnackBar(data: any): void {
    const status = this.getStatus(data);

    this.showNotification({
      title: '',
      subtitle: status.message,
      type: status.className
    });
  }

  getStatus(data: any): any {
    let invalidGrant: string;
    switch (data.status) {
      case 404:
        return {
          className: 'error',
          message: this.errorCodes.notFound
        };
      case 400:
        invalidGrant = this.checkInvalidGrant(data);
        return {
          className: 'error',
          message: invalidGrant ? invalidGrant : this.errorCodes.badRequest
        };
      case 409:
        return {
          className: 'error',
          message: this.errorCodes.conflict
        };
      case 500:
        return {
          className: 'error',
          message: this.errorCodes.internalServerError
        };
      default:
        return {
          className: 'error',
          message: this.errorCodes.internalServerError
        };
    }
  }

  openSnackBar(className: string, message: string): void {
    this.config.panelClass = [className];
    this.config.duration = 3500;
    this.config.horizontalPosition = this.horizontalPosition;
    this.config.verticalPosition = this.verticalPosition;
    this.snackBar.open(message, '', this.config);
  }

  checkInvalidGrant(data: any): string {
    let message!: string;
    if (data.error.error === 'invalid_grant') {
      message = 'The current password is incorrect.';
    } else if (data.error.detail === "'password' is not valid.") {
      message = 'The new password does not meet the password guidelines.';
    }
    return message;
  }

  showNotification(data: NotificationData) {
    this.notificationSubject.next(data);
  }
}
